import React from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Avatar from '../../components/Avatar';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SummaryCollapsibleContent from '../../components/SummaryCollapsibleContent';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import AdminBadge from '../AdminBadge';
import UserProfileSettingsModal from '../settings/UserProfileSettingsModal';
import userActionPermissionCheck, {
  EDIT_PROFILE,
} from '../userActionPermissionCheck';
import UserBadge from './UserBadge';
import UserProfileButtons from './UserProfileButtons';
import * as styles from './UserProfileHeader.module.scss';

const USER_PROFILE_HEADER_USER_FRAGMENT = gql(`
  fragment UserProfileHeader_user on User {
    id
    name
    title
    description
    ...UserBadge_user
    ...UserProfileButtons_user
    ...userActionPermissionCheck_user
    ...UserProfileSettingsModal_user
    ...Avatar_user
  }
`);

interface UserProfileHeaderProps {
  user: DocumentType<typeof USER_PROFILE_HEADER_USER_FRAGMENT>;
}

const UserProfileHeader = ({ user }: UserProfileHeaderProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const isUserProfileCollapsed = useAppSelector(
    (state) => state.ui.isUserProfileCollapsed,
  );
  const handleAdminEdit = () =>
    dispatch(UIActions.openModal(UserProfileSettingsModal, { user }));

  const menuItems =
    currentUser.id === user.id ? (
      <>
        <MenuItem to="/settings/profile">Edit my profile</MenuItem>
        <MenuItem to="/settings">Edit my account settings</MenuItem>
      </>
    ) : (
      userActionPermissionCheck(EDIT_PROFILE, user, currentUser) && (
        <MenuItem
          onClick={handleAdminEdit}
          right={currentUser.id !== user.id ? <AdminBadge /> : null}
        >
          Edit profile
        </MenuItem>
      )
    );

  return (
    <div className={styles.profile}>
      <div className={styles.desktopAvatarContainer}>
        <Avatar user={user} size="large" />
      </div>
      <div className={styles.main}>
        <div className={styles.header}>
          <div className={styles.mobileAvatarContainer}>
            <Avatar user={user} size="medium" />
          </div>
          <div className={styles.nameAndTitle}>
            <div className={styles.name}>
              {user.name}
              <div className={styles.badges}>
                <UserBadge user={user} />
              </div>
            </div>
            <div className={styles.title}>{user.title}</div>
          </div>
          {menuItems && (
            <div className={styles.menuContainer}>
              <OverflowMenu>{menuItems}</OverflowMenu>
            </div>
          )}
        </div>
        <div className={styles.description}>
          <SummaryCollapsibleContent
            collapsed={isUserProfileCollapsed}
            onCollapsedChange={(value) => {
              dispatch(UIActions.toggleUserProfileCollapsed(value));
            }}
            content={user.description}
            maxLines={6}
            typography="postWithNoTitleLine"
          />
        </div>
        <div className={styles.buttons}>
          <UserProfileButtons user={user} />
        </div>
      </div>
    </div>
  );
};

export default UserProfileHeader;
